import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import Header from "@/components/Header";
import AdminTabsNav from "@/components/AdminTabsNav";
import SettlementHealth from "@/components/SettlementHealth";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  ShieldAlert,
  RefreshCw,
  X,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Copy,
  Vault,
  Send,
  Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtMicro(v) {
  const n = (Number(v) || 0) / 1_000_000;
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}
function fmtSigned(v) {
  const n = (Number(v) || 0) / 1_000_000;
  const sign = n >= 0 ? "+" : "";
  return `${sign}${n.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
}
// Adaptive USD formatter — picks enough decimals to actually show sub-cent
// prices (STXOSHI-PERP mark ≈ $0.0000030500 would render as "$0" with a
// fixed 4-decimal cap). Returns "—" when the price is missing.
function fmtUsd(v) {
  const n = Number(v);
  if (!v || !Number.isFinite(n)) return "—";
  let maxDp = 2;
  const abs = Math.abs(n);
  if (abs >= 1000) maxDp = 2;
  else if (abs >= 1) maxDp = 4;
  else if (abs >= 0.01) maxDp = 6;
  else if (abs >= 0.0001) maxDp = 8;
  else maxDp = 10;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: maxDp })}`;
}
function trunc(a, n = 8) {
  if (!a) return "—";
  return `${a.slice(0, n)}…${a.slice(-4)}`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return "—";
  }
}
async function copyText(t) {
  try {
    await navigator.clipboard.writeText(t);
    toast.success("Copied");
  } catch { /* noop */ }
}

/**
 * AdminPerps — one-stop admin view for the STXOSHI Perps market.
 * Sections:
 *  1. Ops summary strip (open interest, house edge collected, largest positions)
 *  2. Positions viewer with filters + inline Force-Close action
 *  3. Manual balance adjustment form
 *  4. Fills audit log (open/close/liquidation events)
 *  5. Balance-adjustment audit log
 */
export default function AdminPerps() {
  const { getAuthHeaders, isConnected, addresses } = useWallet();
  const { showUsd, stxoshiUsd } = useCurrency();
  // USD-aware amount formatters (shadow the module-level ones) so the global
  // STXOSHI/USD toggle flips Notional & P&L across positions and fills.
  const _rateAP = stxoshiUsd || 0.0000037;
  const _usdAP = (micro) => ((Number(micro) || 0) / 1_000_000) * _rateAP;
  const _fmtUsdAmt = (u) => `$${u.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: Math.abs(u) < 0.01 ? 8 : 2 })}`;
  const fmtMicro = (v) =>
    showUsd ? _fmtUsdAmt(_usdAP(v)) : ((Number(v) || 0) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const fmtSigned = (v) => {
    const n = (Number(v) || 0) / 1_000_000;
    const s = n >= 0 ? "+" : "";
    if (showUsd) return `${n < 0 ? "-" : s}${_fmtUsdAmt(Math.abs(_usdAP(v)))}`;
    return `${s}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };
  const connectedStx = addresses?.stx || "";
  const unitLabel = showUsd ? "USD" : "STXOSHI";
  const [ops, setOps] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [sweepOpen, setSweepOpen] = useState(false);
  const [sweepDest, setSweepDest] = useState("");
  const [positions, setPositions] = useState([]);
  const [walletStats, setWalletStats] = useState(null);
  const [fills, setFills] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [posFilters, setPosFilters] = useState({ user_address: "", market: "", status: "open" });
  const [fillFilters, setFillFilters] = useState({ user_address: "", market: "", kind: "" });
  const [adj, setAdj] = useState({ user_address: "", delta_stxoshi: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [closingId, setClosingId] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState("all");
  const [closingUser, setClosingUser] = useState(null);

  // ---- fetchers ----
  const fetchAll = useCallback(async () => {
    if (!isConnected) return;
    setLoading(true);
    try {
      const H = getAuthHeaders();
      const [opsR, treasR, posR, fillR, adjR, statsR] = await Promise.all([
        fetch(`${API}/api/admin/perps/ops-summary`, { headers: H }),
        fetch(`${API}/api/admin/perps/treasury`, { headers: H }),
        fetch(
          `${API}/api/admin/perps/positions?${new URLSearchParams(
            Object.fromEntries(Object.entries(posFilters).filter(([, v]) => v))
          )}`,
          { headers: H }
        ),
        fetch(
          `${API}/api/admin/perps/fills?${new URLSearchParams(
            Object.fromEntries(Object.entries(fillFilters).filter(([, v]) => v))
          )}&limit=100`,
          { headers: H }
        ),
        fetch(`${API}/api/admin/perps/adjustments?limit=50`, { headers: H }),
        fetch(`${API}/api/admin/perps/wallet-stats?period=${statsPeriod}`, { headers: H }),
      ]);
      if (opsR.status === 403) {
        toast.error("Admin access required — connect an admin wallet");
        return;
      }
      const [opsD, treasD, posD, fillD, adjD, statsD] = await Promise.all([
        opsR.json(), treasR.json(), posR.json(), fillR.json(), adjR.json(), statsR.json(),
      ]);
      setOps(opsD);
      setTreasury(treasD);
      setPositions(posD.positions || []);
      setFills(fillD.fills || []);
      setAdjustments(adjD.adjustments || []);
      setWalletStats(statsD);
    } catch (e) {
      toast.error(`Refresh failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [isConnected, getAuthHeaders, posFilters, fillFilters, statsPeriod]);

  useEffect(() => {
    if (isConnected) fetchAll();
  }, [isConnected, fetchAll]);

  // Live-ish auto-refresh for ops + open positions every 15s.
  useEffect(() => {
    if (!isConnected) return;
    const t = setInterval(fetchAll, 15000);
    return () => clearInterval(t);
  }, [isConnected, fetchAll]);

  // ---- actions ----
  async function forceCloseUser(address) {
    const reason = window.prompt(
      `Force-close ALL open positions for ${trunc(address)}? Enter a reason (audited):`
    );
    if (!reason || !reason.trim()) return;
    setClosingUser(address);
    try {
      const r = await fetch(
        `${API}/api/admin/perps/users/${address}/force-close-all?reason=${encodeURIComponent(reason)}`,
        { method: "POST", headers: getAuthHeaders() }
      );
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Bulk close failed"); return; }
      toast.success(`Closed ${d.closed} position(s). Net P&L: ${fmtSigned(d.net_pnl_stxoshi)}`);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setClosingUser(null);
    }
  }

  async function forceClose(positionId) {
    const reason = window.prompt(
      "Reason for force-closing this position (required, will be audited):"
    );
    if (!reason || !reason.trim()) return;
    setClosingId(positionId);
    try {
      const r = await fetch(
        `${API}/api/admin/perps/positions/${positionId}/force-close?reason=${encodeURIComponent(reason)}`,
        { method: "POST", headers: getAuthHeaders() }
      );
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Force-close failed");
        return;
      }
      const net = d.net_pnl_stxoshi || 0;
      const sign = net >= 0 ? "+" : "";
      toast.success(`Position closed. Net P&L: ${sign}${fmtMicro(net)} ${unitLabel}`);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setClosingId(null);
    }
  }

  // Save a CSV file — iOS-friendly (Web Share with a File) + anchor fallback,
  // mirroring the wallet-data export.
  async function saveCsv(csv, filename, title) {
    try {
      const file = new File([csv], filename, { type: "text/csv" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
    }
    try {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open("data:text/csv;charset=utf-8," + encodeURIComponent(csv), "_blank");
    }
  }

  const csvEsc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

  function exportPositionsCsv() {
    if (!positions.length) {
      toast.info("No positions to export.");
      return;
    }
    const header = ["User", "Market", "Side", "Leverage", "Status", "Opened", "Closed", "Notional (STXOSHI)", "Entry (USD)", "Mark/Close (USD)", "P&L (STXOSHI)", "Position ID"];
    const rows = positions.map((p) => {
      const isOpen = p.status === "open";
      const pnl = isOpen ? p.unrealized_pnl_stxoshi : p.realized_pnl_stxoshi;
      return [
        csvEsc(p.user_address), csvEsc(p.market), csvEsc(p.side), csvEsc(p.leverage),
        csvEsc(p.status), csvEsc(p.opened_at || p.created_at || ""), csvEsc(p.closed_at || ""),
        csvEsc((p.notional_stxoshi || 0) / 1_000_000), csvEsc(p.entry_price_usd),
        csvEsc(isOpen ? p.mark_price_usd : p.close_price_usd),
        csvEsc((pnl || 0) / 1_000_000), csvEsc(p.id),
      ].join(",");
    });
    const csv = [header.join(","), ...rows].join("\n");
    saveCsv(csv, `perps-positions-${new Date().toISOString().slice(0, 10)}.csv`, "Perps Positions");
  }

  function exportFillsCsv() {
    if (!fills.length) {
      toast.info("No fills to export.");
      return;
    }
    const header = ["Event Time", "Originally Opened", "User", "Kind", "Market", "Notional (STXOSHI)", "Price (USD)", "Net P&L (STXOSHI)", "Position ID"];
    const rows = fills.map((f) => [
      csvEsc(f.timestamp || ""), csvEsc(f.kind === "open" ? "" : (f.opened_at || "")),
      csvEsc(f.user_address), csvEsc(f.kind), csvEsc(f.market),
      csvEsc((f.notional_stxoshi || 0) / 1_000_000), csvEsc(f.price_usd),
      csvEsc(f.net_pnl_stxoshi != null ? f.net_pnl_stxoshi / 1_000_000 : ""), csvEsc(f.position_id || ""),
    ].join(","));
    const csv = [header.join(","), ...rows].join("\n");
    saveCsv(csv, `perps-fills-${new Date().toISOString().slice(0, 10)}.csv`, "Perps Fills Log");
  }

  async function submitAdjust() {
    const delta = parseInt(adj.delta_stxoshi, 10);
    if (!adj.user_address.trim()) return toast.error("user_address required");
    if (!Number.isFinite(delta) || delta === 0) return toast.error("delta_stxoshi must be a non-zero integer");
    if (!adj.reason.trim()) return toast.error("reason required");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/perps/adjust-balance`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          user_address: adj.user_address.trim(),
          delta_stxoshi: delta,
          reason: adj.reason.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Adjustment failed");
        return;
      }
      toast.success(
        `Balance ${delta >= 0 ? "credited" : "debited"} — new balance ${fmtMicro(d.new_balance_stxoshi)} ${unitLabel}`
      );
      setAdj({ user_address: "", delta_stxoshi: "", reason: "" });
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  function openSweepDialog() {
    const amount = treasury?.balance_stxoshi || 0;
    if (amount <= 0) {
      toast.info("Treasury is empty — nothing to sweep");
      return;
    }
    const hotWallet = treasury?.hot_wallet_address || "";
    const defaultDest = treasury?.default_sweep_destination || "";
    // Prefer: connected admin wallet (if it's not the hot wallet) → configured default (if safe) → empty
    let suggested = "";
    if (connectedStx && connectedStx !== hotWallet) {
      suggested = connectedStx;
    } else if (defaultDest && defaultDest !== hotWallet) {
      suggested = defaultDest;
    }
    setSweepDest(suggested);
    setSweepOpen(true);
  }

  async function submitSweep() {
    const amount = treasury?.balance_stxoshi || 0;
    const hotWallet = treasury?.hot_wallet_address || "";
    const cleanDest = (sweepDest || "").trim();
    if (!cleanDest.startsWith("SP") || cleanDest.length < 30) {
      toast.error("Invalid destination — must be a mainnet Stacks address (starts with SP...)");
      return;
    }
    if (hotWallet && cleanDest === hotWallet) {
      toast.error("Destination equals the hot wallet — sweep would fail on-chain.");
      return;
    }
    setSweeping(true);
    try {
      const r = await fetch(`${API}/api/admin/perps/treasury/sweep`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ destination_address: cleanDest }),
      });
      const d = await r.json();
      if (!r.ok) {
        const detail = d?.detail;
        const msg = typeof detail === "string"
          ? detail
          : (detail?.message || detail?.error || "Sweep failed");
        const explorerUrl = detail?.explorer_url;
        toast.error(`Sweep failed: ${msg}`, {
          description: (detail?.hint || "Treasury balance restored — safe to retry."),
          action: explorerUrl
            ? { label: "View tx", onClick: () => window.open(explorerUrl, "_blank", "noopener") }
            : undefined,
          duration: 12000,
        });
        return;
      }
      const explorerUrl = d.explorer_url || (d.txid ? `https://explorer.hiro.so/txid/${d.txid}?chain=mainnet` : null);
      if (d.status === "pending") {
        toast.warning(
          `Sweep pending on-chain: ${fmtMicro(d.swept_stxoshi)} ${unitLabel}`,
          {
            description: `TXID: ${d.txid?.slice(0, 12)}… — verify on Hiro Explorer, then reconcile.`,
            action: { label: "View", onClick: () => window.open(explorerUrl, "_blank", "noopener") },
            duration: 15000,
          }
        );
      } else {
        toast.success(
          `Confirmed on-chain: ${fmtMicro(d.swept_stxoshi)} ${unitLabel} → ${trunc(d.destination, 10)}`,
          explorerUrl
            ? {
                description: `TXID: ${d.txid?.slice(0, 12)}…`,
                action: {
                  label: "View",
                  onClick: () => window.open(explorerUrl, "_blank", "noopener"),
                },
                duration: 12000,
              }
            : undefined
        );
      }
      setSweepOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSweeping(false);
    }
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="p-4 max-w-6xl mx-auto">
          <AdminTabsNav active="perps" />
          <Card>
            <CardContent className="p-12 text-center text-muted-foreground">
              Connect your admin wallet to access Perps ops.
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="p-3 sm:p-4 max-w-6xl mx-auto pb-24">
        <AdminTabsNav active="perps" />

        {/* Ops summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="admin-perps-ops-strip">
          <Card>
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Open positions</p>
              <p className="font-mono text-xl font-bold" data-testid="ops-open-count">
                {ops?.open_positions ?? "—"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Open interest</p>
              <p className="font-mono text-xl font-bold" data-testid="ops-open-interest">
                {ops ? fmtMicro(ops.open_interest_stxoshi) : "—"}
              </p>
              <p className="text-[10px] text-muted-foreground">{unitLabel} notional</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Edge — 24h</p>
              <p className="font-mono text-xl font-bold text-orange-400" data-testid="ops-edge-24h">
                {ops ? fmtMicro(ops.house_edge_24h_stxoshi) : "—"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Edge — 7d</p>
              <p className="font-mono text-xl font-bold text-orange-400" data-testid="ops-edge-7d">
                {ops ? fmtMicro(ops.house_edge_7d_stxoshi) : "—"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Settlement Health */}
        <SettlementHealth />

        {/* House Treasury */}
        <Card className="mb-4 border-orange-500/40" data-testid="admin-treasury-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Vault className="w-4 h-4 text-orange-400" />
              House Treasury
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Balance</p>
                <p className="font-mono text-xl font-bold text-orange-400" data-testid="treasury-balance">
                  {treasury ? fmtMicro(treasury.balance_stxoshi) : "—"}
                </p>
                <p className="text-[10px] text-muted-foreground">{unitLabel} · available to sweep</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Lifetime liquidations</p>
                <p className="font-mono text-lg" data-testid="treasury-liq-total">
                  {treasury ? fmtMicro(treasury.cumulative_liquidations) : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Lifetime house edge</p>
                <p className="font-mono text-lg" data-testid="treasury-edge-total">
                  {treasury ? fmtMicro(treasury.cumulative_house_edge) : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Lifetime swept</p>
                <p className="font-mono text-lg" data-testid="treasury-swept-total">
                  {treasury ? fmtMicro(treasury.cumulative_swept) : "—"}
                </p>
              </div>
            </div>

            {treasury?.default_sweep_destination && (
              <div className="text-[11px] font-mono text-muted-foreground">
                Sweep destination:{" "}
                <button
                  onClick={() => copyText(treasury.default_sweep_destination)}
                  className="inline-flex items-center gap-1 hover:text-orange-400"
                  title={treasury.default_sweep_destination}
                >
                  {trunc(treasury.default_sweep_destination, 10)}
                  <Copy className="w-3 h-3 opacity-50" />
                </button>
                {treasury?.hot_wallet_address && (
                  <>
                    {" · "}
                    Hot wallet:{" "}
                    <button
                      onClick={() => copyText(treasury.hot_wallet_address)}
                      className="inline-flex items-center gap-1 hover:text-orange-400"
                      title={treasury.hot_wallet_address}
                    >
                      {trunc(treasury.hot_wallet_address, 10)}
                      <Copy className="w-3 h-3 opacity-50" />
                    </button>
                  </>
                )}
              </div>
            )}

            {treasury?.self_transfer_conflict && (
              <div
                className="text-[11px] p-2 rounded border border-red-500/50 bg-red-500/10 text-red-200"
                data-testid="treasury-self-transfer-warning"
              >
                <ShieldAlert className="w-3 h-3 inline mr-1" />
                Configured sweep destination matches the hot wallet — the default sweep would
                fail on-chain with a post-condition error. Click Sweep and enter a different
                Stacks address.
              </div>
            )}

            <Button
              onClick={openSweepDialog}
              disabled={sweeping || !treasury || treasury.balance_stxoshi <= 0}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
              data-testid="treasury-sweep-btn"
            >
              {sweeping ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sweeping…</>
              ) : (
                <><Send className="w-4 h-4 mr-1" /> Sweep all to Treasury Wallet</>
              )}
            </Button>
          </CardContent>
        </Card>

        <Dialog open={sweepOpen} onOpenChange={(v) => !sweeping && setSweepOpen(v)}>
          <DialogContent className="max-w-md" data-testid="sweep-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-4 h-4 text-orange-400" />
                Sweep House Treasury on-chain
              </DialogTitle>
              <DialogDescription>
                Broadcast a real STXOSHI SIP-010 transfer from the hot wallet.
                The transaction is signed by the backend hot wallet — you only need to authorize the sweep as an admin.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 items-center">
                <span className="text-xs text-muted-foreground">Amount</span>
                <span className="col-span-2 font-mono text-orange-400 text-lg font-bold">
                  {fmtMicro(treasury?.balance_stxoshi)} {unitLabel}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center">
                <span className="text-xs text-muted-foreground">From (hot wallet)</span>
                <span className="col-span-2 font-mono text-[11px] break-all text-muted-foreground">
                  {treasury?.hot_wallet_address || "—"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 items-start">
                <label htmlFor="sweep-dest-input" className="text-xs text-muted-foreground pt-2">
                  Destination
                </label>
                <div className="col-span-2 space-y-1">
                  <Input
                    id="sweep-dest-input"
                    data-testid="sweep-dest-input"
                    value={sweepDest}
                    onChange={(e) => setSweepDest(e.target.value)}
                    placeholder="SP..."
                    className="font-mono text-xs"
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                  {connectedStx && connectedStx !== treasury?.hot_wallet_address && (
                    <button
                      type="button"
                      onClick={() => setSweepDest(connectedStx)}
                      className="text-[10px] text-orange-400 hover:underline"
                      data-testid="sweep-dest-use-connected"
                    >
                      Use my connected admin wallet ({trunc(connectedStx, 8)})
                    </button>
                  )}
                </div>
              </div>
              {sweepDest && sweepDest === treasury?.hot_wallet_address && (
                <p className="text-[11px] text-red-400">
                  ⚠ This equals the hot wallet — self-transfer would fail on-chain. Pick a different address.
                </p>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setSweepOpen(false)}
                disabled={sweeping}
                data-testid="sweep-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submitSweep}
                disabled={
                  sweeping ||
                  !sweepDest ||
                  !sweepDest.startsWith("SP") ||
                  sweepDest === treasury?.hot_wallet_address
                }
                className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
                data-testid="sweep-confirm"
              >
                {sweeping ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Broadcasting…</>
                ) : (
                  <>Broadcast on-chain</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Wallet performance stats: biggest winners / losers + long/short counts */}
        <Card className="mb-4" data-testid="admin-perps-wallet-stats">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-orange-400" />
              Trader Stats
            </CardTitle>
            <div className="flex gap-1" data-testid="perps-stats-period">
              {["today", "7d", "all"].map((p) => (
                <button
                  key={p}
                  onClick={() => setStatsPeriod(p)}
                  className={`text-[10px] uppercase px-2 py-1 rounded-md border ${
                    statsPeriod === p
                      ? "bg-orange-600 text-white border-orange-600"
                      : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                  data-testid={`perps-stats-period-${p}`}
                >
                  {p === "today" ? "24h" : p === "7d" ? "7D" : "All"}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="rounded-lg border border-border p-2">
                <p className="text-[9px] uppercase text-muted-foreground">Traders</p>
                <p className="text-lg font-bold" data-testid="perps-stats-traders">{walletStats?.totals?.traders ?? "—"}</p>
              </div>
              <div className="rounded-lg border border-border p-2">
                <p className="text-[9px] uppercase text-muted-foreground">Longs / Shorts</p>
                <p className="text-sm font-bold"><span className="text-green-400">{walletStats?.totals?.total_long ?? 0}</span> / <span className="text-red-400">{walletStats?.totals?.total_short ?? 0}</span></p>
              </div>
              <div className="rounded-lg border border-border p-2">
                <p className="text-[9px] uppercase text-muted-foreground">Treasury Net</p>
                <p className={`text-sm font-bold ${(walletStats?.totals?.treasury_net_stxoshi ?? 0) >= 0 ? "text-green-400" : "text-red-400"}`}>{fmtSigned(walletStats?.totals?.treasury_net_stxoshi)}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Traders whose NET P&L is positive = they are winning = house is losing */}
              <div data-testid="perps-stats-winners">
                <p className="text-xs uppercase text-muted-foreground mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3 text-green-400" /> Biggest Trader Wins <span className="normal-case text-[10px]">(house pays)</span></p>
                <div className="space-y-1 max-h-[40vh] overflow-y-auto">
                  {(walletStats?.winners || []).length === 0 && <p className="text-xs text-muted-foreground py-2">No net-winning traders.</p>}
                  {(walletStats?.winners || []).map((w) => (
                    <div key={w.address} className="rounded-md border border-border p-2 text-xs font-mono" data-testid={`perps-winner-${w.address}`}>
                      <div className="flex justify-between gap-2">
                        <button onClick={() => copyText(w.address)} className="inline-flex items-center gap-1 hover:text-orange-400 truncate" title={w.address}>{trunc(w.address)} <Copy className="w-3 h-3 opacity-40" /></button>
                        <span className="text-green-400 font-bold">{fmtSigned(w.net_pnl_stxoshi)}</span>
                      </div>
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                        <span><span className="text-green-400">{w.long_count}L</span> / <span className="text-red-400">{w.short_count}S</span> · {w.positions} pos</span>
                        <span>WR {w.win_rate}% · {w.avg_leverage}× · best {fmtSigned(w.biggest_win_stxoshi)}</span>
                      </div>
                      {w.open_count > 0 && (
                        <div className="mt-1.5">
                          <Button size="sm" variant="outline"
                            onClick={() => forceCloseUser(w.address)}
                            disabled={closingUser === w.address}
                            className="h-6 px-2 text-[10px] border-red-500/40 text-red-400 w-full"
                            data-testid={`perps-winner-close-${w.address}`}>
                            {closingUser === w.address ? <Loader2 className="w-3 h-3 animate-spin" /> : <><X className="w-3 h-3 mr-1" /> Force-close {w.open_count} open</>}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div data-testid="perps-stats-losers">
                <p className="text-xs uppercase text-muted-foreground mb-2 flex items-center gap-1"><TrendingDown className="w-3 h-3 text-red-400" /> Biggest Trader Losses <span className="normal-case text-[10px]">(house keeps)</span></p>
                <div className="space-y-1 max-h-[40vh] overflow-y-auto">
                  {(walletStats?.losers || []).length === 0 && <p className="text-xs text-muted-foreground py-2">No net-losing traders.</p>}
                  {(walletStats?.losers || []).map((w) => (
                    <div key={w.address} className="rounded-md border border-border p-2 text-xs font-mono" data-testid={`perps-loser-${w.address}`}>
                      <div className="flex justify-between gap-2">
                        <button onClick={() => copyText(w.address)} className="inline-flex items-center gap-1 hover:text-orange-400 truncate" title={w.address}>{trunc(w.address)} <Copy className="w-3 h-3 opacity-40" /></button>
                        <span className="text-red-400 font-bold">{fmtSigned(w.net_pnl_stxoshi)}</span>
                      </div>
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                        <span><span className="text-green-400">{w.long_count}L</span> / <span className="text-red-400">{w.short_count}S</span> · {w.positions} pos</span>
                        <span>WR {w.win_rate}% · {w.avg_leverage}× · worst {fmtSigned(w.biggest_loss_stxoshi)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-orange-400" />
              Positions
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={exportPositionsCsv}
                data-testid="admin-perps-export-positions"
                className="h-8 px-2 text-[11px]"
              >
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchAll}
                disabled={loading}
                data-testid="admin-perps-refresh"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              <Input
                placeholder="Filter by user_address"
                value={posFilters.user_address}
                onChange={(e) => setPosFilters({ ...posFilters, user_address: e.target.value })}
                data-testid="admin-perps-filter-user"
                className="font-mono text-xs"
              />
              <select
                className="rounded-md border border-border bg-background text-xs px-2 py-2"
                value={posFilters.market}
                onChange={(e) => setPosFilters({ ...posFilters, market: e.target.value })}
                data-testid="admin-perps-filter-market"
              >
                <option value="">All markets</option>
                <option value="BTC">BTC</option>
                <option value="STX">STX</option>
                <option value="STXOSHI">STXOSHI</option>
              </select>
              <select
                className="rounded-md border border-border bg-background text-xs px-2 py-2"
                value={posFilters.status}
                onChange={(e) => setPosFilters({ ...posFilters, status: e.target.value })}
                data-testid="admin-perps-filter-status"
              >
                <option value="">All statuses</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="liquidated">Liquidated</option>
              </select>
            </div>

            {positions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center" data-testid="admin-perps-empty">
                No positions match the current filter.
              </p>
            ) : (
              <>
                {/* Mobile: stacked cards (no horizontal scroll on iPhone) */}
                <div className="sm:hidden space-y-2 max-h-[65vh] overflow-y-auto pr-1" data-testid="admin-perps-positions-mobile">
                  {positions.map((p) => {
                    const isOpen = p.status === "open";
                    const pnl = isOpen ? p.unrealized_pnl_stxoshi : p.realized_pnl_stxoshi;
                    const positive = (pnl || 0) >= 0;
                    const priceShown = isOpen ? p.mark_price_usd : p.close_price_usd;
                    return (
                      <div
                        key={p.id}
                        className="rounded-lg border border-border p-3 text-xs font-mono space-y-2"
                        data-testid={`admin-perps-row-${p.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <button
                            onClick={() => copyText(p.user_address)}
                            className="inline-flex items-center gap-1 hover:text-orange-400 truncate"
                            title={p.user_address}
                          >
                            {trunc(p.user_address)}
                            <Copy className="w-3 h-3 opacity-40 shrink-0" />
                          </button>
                          <Badge
                            variant="outline"
                            className={`text-[10px] uppercase shrink-0 ${
                              p.side === "long"
                                ? "border-green-500/40 text-green-400"
                                : "border-red-500/40 text-red-400"
                            }`}
                          >
                            {p.market} {p.side} {p.leverage}×
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          <div>
                            <p className="text-[9px] uppercase text-muted-foreground">Notional</p>
                            <p>{fmtMicro(p.notional_stxoshi)}</p>
                          </div>
                          <div>
                            <p className="text-[9px] uppercase text-muted-foreground">P&amp;L</p>
                            <p className={positive ? "text-green-400" : "text-red-400"}>
                              {fmtSigned(pnl)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[9px] uppercase text-muted-foreground">Entry</p>
                            <p>{fmtUsd(p.entry_price_usd)}</p>
                          </div>
                          <div>
                            <p className="text-[9px] uppercase text-muted-foreground">
                              {isOpen ? "Mark" : "Close"}
                            </p>
                            <p>{fmtUsd(priceShown)}</p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/40">
                          <div className="flex flex-col gap-0.5 min-w-0">
                            {p.status === "liquidated" ? (
                              <span className="text-red-400 uppercase text-[10px]">liquidated</span>
                            ) : p.status === "closed" ? (
                              <span className="text-muted-foreground uppercase text-[10px]">closed</span>
                            ) : (
                              <span className="text-green-400 uppercase text-[10px]">open</span>
                            )}
                            <span className="text-[9px] text-muted-foreground" data-testid={`admin-perps-opened-${p.id}`}>
                              Opened {fmtDateTime(p.opened_at || p.created_at)}
                            </span>
                          </div>
                          {isOpen ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => forceClose(p.id)}
                              disabled={closingId === p.id}
                              className="h-7 px-2 text-[10px]"
                              data-testid={`admin-perps-force-close-${p.id}`}
                            >
                              {closingId === p.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <X className="w-3 h-3 mr-1" />
                                  Force close
                                </>
                              )}
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-[10px]">—</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop / tablet: dense table view */}
                <div className="hidden sm:block overflow-x-auto max-h-[65vh] overflow-y-auto" data-testid="admin-perps-positions-desktop">
                <table className="w-full text-xs font-mono" data-testid="admin-perps-positions-table">
                  <thead className="text-muted-foreground uppercase text-[10px]">
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-2">User</th>
                      <th className="text-left pr-2">Market</th>
                      <th className="text-left pr-2">Side</th>
                      <th className="text-left pr-2">Opened</th>
                      <th className="text-right pr-2">Notional</th>
                      <th className="text-right pr-2">Entry</th>
                      <th className="text-right pr-2">Mark / Close</th>
                      <th className="text-right pr-2">P&amp;L</th>
                      <th className="text-left pr-2">Status</th>
                      <th className="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((p) => {
                      const isOpen = p.status === "open";
                      const pnl = isOpen ? p.unrealized_pnl_stxoshi : p.realized_pnl_stxoshi;
                      const positive = (pnl || 0) >= 0;
                      const priceShown = isOpen ? p.mark_price_usd : p.close_price_usd;
                      return (
                        <tr
                          key={p.id}
                          className="border-b border-border/40 hover:bg-muted/30"
                          data-testid={`admin-perps-row-desk-${p.id}`}
                        >
                          <td className="py-2 pr-2">
                            <button
                              onClick={() => copyText(p.user_address)}
                              className="hover:text-orange-400 inline-flex items-center gap-1"
                              title={p.user_address}
                            >
                              {trunc(p.user_address)}
                              <Copy className="w-3 h-3 opacity-40" />
                            </button>
                          </td>
                          <td className="pr-2">{p.market}</td>
                          <td className="pr-2">
                            <Badge
                              variant="outline"
                              className={`text-[10px] uppercase ${
                                p.side === "long"
                                  ? "border-green-500/40 text-green-400"
                                  : "border-red-500/40 text-red-400"
                              }`}
                            >
                              {p.side} {p.leverage}×
                            </Badge>
                          </td>
                          <td className="pr-2 whitespace-nowrap text-muted-foreground" data-testid={`admin-perps-opened-desk-${p.id}`}>
                            {fmtDateTime(p.opened_at || p.created_at)}
                          </td>
                          <td className="pr-2 text-right">{fmtMicro(p.notional_stxoshi)}</td>
                          <td className="pr-2 text-right">{fmtUsd(p.entry_price_usd)}</td>
                          <td className="pr-2 text-right">{fmtUsd(priceShown)}</td>
                          <td className={`pr-2 text-right ${positive ? "text-green-400" : "text-red-400"}`}>
                            {fmtSigned(pnl)}
                          </td>
                          <td className="pr-2">
                            {p.status === "liquidated" ? (
                              <span className="text-red-400 uppercase text-[10px]">liquidated</span>
                            ) : p.status === "closed" ? (
                              <span className="text-muted-foreground uppercase text-[10px]">closed</span>
                            ) : (
                              <span className="text-green-400 uppercase text-[10px]">open</span>
                            )}
                          </td>
                          <td className="text-right">
                            {isOpen ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => forceClose(p.id)}
                                disabled={closingId === p.id}
                                className="h-7 px-2 text-[10px]"
                                data-testid={`admin-perps-force-close-desk-${p.id}`}
                              >
                                {closingId === p.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <X className="w-3 h-3 mr-1" />
                                    Force close
                                  </>
                                )}
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-[10px]">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Balance adjust */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-orange-400" />
              Manual balance adjustment
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Credit (positive) or debit (negative) micro-STXOSHI on a user&apos;s site wallet.
              1 STXOSHI = 1,000,000 micro. Debits refuse if they would overdraw. Every action is
              logged with your admin wallet address.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Input
                placeholder="user_address (SP...)"
                value={adj.user_address}
                onChange={(e) => setAdj({ ...adj, user_address: e.target.value })}
                data-testid="admin-perps-adj-user"
                className="font-mono text-xs md:col-span-2"
              />
              <Input
                type="number"
                placeholder="delta (micro-STXOSHI, +/-)"
                value={adj.delta_stxoshi}
                onChange={(e) => setAdj({ ...adj, delta_stxoshi: e.target.value })}
                data-testid="admin-perps-adj-delta"
                className="font-mono text-xs"
              />
              <Input
                placeholder="reason"
                value={adj.reason}
                onChange={(e) => setAdj({ ...adj, reason: e.target.value })}
                data-testid="admin-perps-adj-reason"
                className="text-xs"
              />
            </div>
            <Button
              size="sm"
              onClick={submitAdjust}
              disabled={busy}
              data-testid="admin-perps-adj-submit"
            >
              {busy ? "Applying…" : "Apply adjustment"}
            </Button>
          </CardContent>
        </Card>

        {/* Fills audit log */}
        <Card className="mb-4">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-orange-400" />
              Fills audit log
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={exportFillsCsv}
              data-testid="admin-perps-export-fills"
              className="h-8 px-2 text-[11px]"
            >
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              <Input
                placeholder="user_address"
                value={fillFilters.user_address}
                onChange={(e) => setFillFilters({ ...fillFilters, user_address: e.target.value })}
                data-testid="admin-perps-fill-filter-user"
                className="font-mono text-xs"
              />
              <select
                className="rounded-md border border-border bg-background text-xs px-2 py-2"
                value={fillFilters.market}
                onChange={(e) => setFillFilters({ ...fillFilters, market: e.target.value })}
                data-testid="admin-perps-fill-filter-market"
              >
                <option value="">All markets</option>
                <option value="BTC">BTC</option>
                <option value="STX">STX</option>
                <option value="STXOSHI">STXOSHI</option>
              </select>
              <select
                className="rounded-md border border-border bg-background text-xs px-2 py-2"
                value={fillFilters.kind}
                onChange={(e) => setFillFilters({ ...fillFilters, kind: e.target.value })}
                data-testid="admin-perps-fill-filter-kind"
              >
                <option value="">All events</option>
                <option value="open">Open</option>
                <option value="close">Close</option>
                <option value="liquidation">Liquidation</option>
              </select>
            </div>
            {fills.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No fills match.</p>
            ) : (
              <div className="overflow-x-auto max-h-[65vh] overflow-y-auto" data-testid="admin-perps-fills-scroll">
                <table className="w-full text-xs font-mono">
                  <thead className="text-muted-foreground uppercase text-[10px] sticky top-0 bg-background z-10">
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-2">Time</th>
                      <th className="text-left pr-2">Opened</th>
                      <th className="text-left pr-2">User</th>
                      <th className="text-left pr-2">Kind</th>
                      <th className="text-left pr-2">Mkt</th>
                      <th className="text-right pr-2">Notional</th>
                      <th className="text-right pr-2">Price</th>
                      <th className="text-right pr-2">Net P&amp;L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fills.map((f) => (
                      <tr key={f.id} className="border-b border-border/40" data-testid={`admin-perps-fill-${f.id}`}>
                        <td className="py-2 pr-2 whitespace-nowrap">{new Date(f.timestamp).toLocaleString()}</td>
                        <td className="pr-2 whitespace-nowrap text-muted-foreground" data-testid={`admin-perps-fill-opened-${f.id}`}>
                          {f.kind === "open" ? "—" : fmtDateTime(f.opened_at)}
                        </td>
                        <td className="pr-2">{trunc(f.user_address)}</td>
                        <td className="pr-2">
                          {f.kind === "liquidation" ? (
                            <span className="text-red-400 uppercase text-[10px] flex items-center gap-1">
                              <ShieldAlert className="w-3 h-3" />
                              liq
                            </span>
                          ) : f.kind === "close" ? (
                            <span className="text-muted-foreground uppercase text-[10px] flex items-center gap-1">
                              <ArrowDownRight className="w-3 h-3" />
                              close
                            </span>
                          ) : (
                            <span className="text-green-400 uppercase text-[10px] flex items-center gap-1">
                              <ArrowUpRight className="w-3 h-3" />
                              open
                            </span>
                          )}
                        </td>
                        <td className="pr-2">{f.market}</td>
                        <td className="pr-2 text-right">{fmtMicro(f.notional_stxoshi)}</td>
                        <td className="pr-2 text-right">${(f.price_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                        <td className={`pr-2 text-right ${(f.net_pnl_stxoshi || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {f.net_pnl_stxoshi != null ? fmtSigned(f.net_pnl_stxoshi) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Balance-adjustment audit log */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-orange-400" />
              Recent balance adjustments
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {adjustments.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No adjustments logged yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead className="text-muted-foreground uppercase text-[10px]">
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-2">Time</th>
                      <th className="text-left pr-2">User</th>
                      <th className="text-right pr-2">Delta</th>
                      <th className="text-left pr-2">Reason</th>
                      <th className="text-left">Admin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjustments.map((a) => (
                      <tr key={a.id} className="border-b border-border/40" data-testid={`admin-perps-adj-${a.id}`}>
                        <td className="py-2 pr-2 whitespace-nowrap">{new Date(a.timestamp).toLocaleString()}</td>
                        <td className="pr-2">{trunc(a.user_address)}</td>
                        <td className={`pr-2 text-right ${a.delta_stxoshi >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {fmtSigned(a.delta_stxoshi)}
                        </td>
                        <td className="pr-2 max-w-xs truncate" title={a.reason}>{a.reason}</td>
                        <td>{trunc(a.admin_actor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
